import React, { ChangeEvent, useContext, useEffect, useState } from "react";
import BreadCrumbs from "../../components/BreadCrumbs/BreadCrumbs";
import { Button, Select, TextInput } from "../../components";
import DateTimePicker from "../../components/DatePicker/DateTimePicker";
import { addRequest } from "../../redux/slices/NotificationAlertSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { ToastContext } from "../../contexts/ToastContext/ToastContext";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { DATE_TIME_FORMAT } from "../../utils/constants";
import MultiSelect from "../../components/Select/MultiSelect";
import { UserType } from "../../services/user.service";

const AddNotification = ({ isAdminPage }: { isAdminPage: boolean }) => {
  interface TemplateData {
    notificationId: string;
    name: string;
    message: string;
    audience: string;
    type: string;
    maintenanceStartWindow: Date | null;
    maintenanceEndWindow: Date | null;
    active: boolean;
    triggeredBy: string;
    roles?: string[];
  }
  const dispatch = useDispatch<AppDispatch>();
  const toast = useContext(ToastContext);
  const user = useSelector((state: RootState) => state.currentUser.currentUser);
  const navigate = useNavigate();

  const [formData, setFormData] = useState<TemplateData>({
    notificationId: "",
    name: "",
    message: "",
    audience: "",
    type: "",
    maintenanceStartWindow: null,
    maintenanceEndWindow: null,
    active: false,
    triggeredBy: user?.firstName + " " + user?.lastName,
    roles: [],
  });
  const [errorInitiated, setErrorInitiated] = useState(false);

  const audience = [{ title: "PATIENT" }, { title: "OFFICE" }];
  const alertType = [{ title: "WARNING" }, { title: "ERROR" }];
  const officeOptions = [
    {
      title: "Office Superuser",
      key: UserType.OfficeAdmin,
    },
    {
      title: "Accounting",
      key: UserType.Accounting,
    },
    {
      title: "User",
      key: UserType.User,
    },
  ];
  // const officeOptions = ["OFFICE SUPERUSER", "ACCOUNTING", "USER"];
  const [selectedOffices, setSelectedOffices] = useState<string[]>([]);

  const handleChange = (e: ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTemplateAction = async () => {
    setErrorInitiated(true);
    const isOfficeAudience = formData.audience === "OFFICE";
    const hasEmptyFields =
      !formData.name.trim() ||
      !formData.message.trim() ||
      !formData.audience ||
      !formData.type ||
      !formData.maintenanceStartWindow ||
      !formData.maintenanceEndWindow;

    const isMessageTooLong = formData.message.length > 100;

    const hasInvalidMaintenanceWindow =
      formData.maintenanceStartWindow &&
      formData.maintenanceEndWindow &&
      new Date(formData.maintenanceEndWindow) <= new Date(formData.maintenanceStartWindow);

    const isOfficeRolesInvalid = isOfficeAudience && (!formData.roles || formData.roles.length === 0);

    if (hasEmptyFields || isMessageTooLong || hasInvalidMaintenanceWindow || isOfficeRolesInvalid) {
      return;
    }
    const payloadData = {
      ...formData,
      maintenanceStartWindow: moment(formData.maintenanceStartWindow).format(DATE_TIME_FORMAT),
      maintenanceEndWindow: moment(formData.maintenanceEndWindow).format(DATE_TIME_FORMAT),
    };

    if (!isOfficeAudience) {
      delete payloadData.roles;
    }
    const res = await dispatch(addRequest({ request: payloadData }));
    if (res.meta.requestStatus === "fulfilled") {
      const payload = res.payload as { data: any; status: number };
      if (payload.status === 200) {
        toast?.openToast("notification created Successfully", 2000);
        navigate("/notification-listing");
      } else {
        toast?.errorToast(payload?.data?.message ? payload?.data?.message : "Something went wrong");
      }
    }
  };
  const isEndBeforeStart =
    formData.maintenanceStartWindow &&
    formData.maintenanceEndWindow &&
    new Date(formData.maintenanceEndWindow) <= new Date(formData.maintenanceStartWindow);

  const handleOfficesChange = (values: string[]) => {
    setFormData(prev => ({ ...prev, roles: values }));
  };

  return (
    <>
      <div className="container mx-auto p-8">
        <BreadCrumbs items={[{ key: "#", title: "Create Notification" }]} />
        <div className="flex flex-col gap-2">
          <div>
            <TextInput
              isError={errorInitiated && !formData.name.trim()}
              value={formData.name}
              name="name"
              onChangeFunc={handleChange}
              label="Notification Name"
              errorMsg="Please provide a notification name"
              isRequired
              placeholder="Name of notification"
            />
          </div>
          <div>
            <TextInput
              isError={errorInitiated && (!formData.message.trim() || formData.message.length > 100)}
              value={formData.message}
              name="message"
              onChangeFunc={handleChange}
              label="Notification Message"
              errorMsg={
                !formData.message.trim()
                  ? "Please provide a notification message"
                  : formData.message.length > 100
                  ? "Message must be under 100 characters"
                  : ""
              }
              isRequired
              placeholder="Notification Message"
            />
          </div>
          <div>
            <Select
              onSelect={handleChange}
              placeHolder="Select audience"
              name="audience"
              options={audience.map(v => v.title)}
              required={true}
              title="Audience"
              errorMsg={errorInitiated && !formData.audience ? "Please select audience" : ""}
            />
          </div>
          {formData?.audience === "OFFICE" && (
            <MultiSelect
              options={officeOptions}
              selectedValues={formData.roles || []}
              onChange={handleOfficesChange}
              title="Select Offices"
              required
              errorMsg={errorInitiated && (!formData.roles || formData.roles.length === 0) ? "Please select at least one office" : ""}
            />
          )}

          <div>
            <Select
              onSelect={handleChange}
              placeHolder="Select type"
              name="type"
              options={alertType.map(v => v.title)}
              required={true}
              title="Type of Notification"
              errorMsg={errorInitiated && !formData.type ? "Please select type" : ""}
            />
          </div>

          {/* Start DateTime */}
          <div>
            <DateTimePicker
              value={formData.maintenanceStartWindow}
              onChange={date => setFormData(prev => ({ ...prev, maintenanceStartWindow: date }))}
              label="Start Date & Time (UTC)"
              error={errorInitiated && !formData.maintenanceStartWindow}
              errorMsg="Please select start date & time"
            />
          </div>

          {/* End DateTime */}
          <div>
            <DateTimePicker
              value={formData.maintenanceEndWindow}
              onChange={date => setFormData(prev => ({ ...prev, maintenanceEndWindow: date }))}
              label="End Date & Time (UTC)"
              error={!!(errorInitiated && (!formData.maintenanceEndWindow || isEndBeforeStart))}
              errorMsg={
                !formData.maintenanceEndWindow ? "Please select end date & time" : isEndBeforeStart ? "End time must be after start time" : ""
              }
            />
          </div>
        </div>

        <div className="w-max">
          <Button className="w-[300px] mt-4" text="Submit" onClick={handleTemplateAction} />
        </div>
      </div>
    </>
  );
};

export default AddNotification;
